import React, { useRef } from 'react';
import { Box, IconButton } from '@mui/material';
import { ChevronLeftSharp, ChevronRightSharp } from '@mui/icons-material';

import { Comment, CommentProps } from './comment';

/* === COMMENTS PROPS ======================================================================= */
interface Props {
  comments?: CommentProps[];
}

export const Comments = ({ comments = [] }: Props) => {
  /* === STATES ======================================================================= */
  const commentsContainerRef = useRef<HTMLDivElement>(null);

  /* === HANDLERS ======================================================================= */
  const scrollTo = (direction: 'left' | 'right') => {
    /**
     * Ensure that comments container ref has value.
     */
    if (!commentsContainerRef?.current) return;

    /**
     * Calculate the offset to scroll based on direction (left | right).
     */
    const offsetToScroll =
      direction === 'left'
        ? commentsContainerRef.current.scrollLeft - commentsContainerRef.current.clientWidth
        : commentsContainerRef.current.scrollLeft + commentsContainerRef.current.clientWidth;

    commentsContainerRef.current.scrollTo?.({
      left: offsetToScroll,
      behavior: 'smooth',
    });
  };

  /* === RENDER ======================================================================= */
  return (
    <Box position="relative" display="flex" height={{ xs: 'fit-content', md: 542 }} alignItems="center">
      <IconButton
        sx={{ position: 'absolute', top: '50%', left: { xs: 16, md: 94 }, zIndex: 10, transform: 'translateY(-50%)', color: 'primary.main', p: 0 }}
        onClick={() => scrollTo('left')}
      >
        <ChevronLeftSharp sx={{ width: 48, height: 48 }} />
      </IconButton>

      <IconButton
        sx={{ position: 'absolute', top: '50%', right: { xs: 16, md: 94 }, zIndex: 10, transform: 'translateY(-50%)', color: 'primary.main', p: 0 }}
        onClick={() => scrollTo('right')}
      >
        <ChevronRightSharp sx={{ width: 48, height: 48 }} />
      </IconButton>

      <Box
        ref={commentsContainerRef}
        position="relative"
        display="flex"
        flexDirection="row"
        sx={{
          overflow: 'auto',
          scrollSnapType: 'x mandatory',
          '& > *': {
            scrollSnapAlign: 'center',
          },
          '::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {comments.map((comment) => (
          <Box minWidth="100%" py={{ xs: 4, md: 0 }} px={{ xs: 12, md: 20 }}>
            <Comment key={comment.user.firstName + comment.user.lastName + comment.user.city} {...comment} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};
